import { makeAutoObservable, runInAction } from 'mobx';
import { STATUS_API } from 'src/config';
import { t } from '../services/LocalizationService';
import type { Timezone } from './UserStore';
import {
  checkLoginAvailable,
  forgetLoginAvailability,
} from '../services/loginAvailability';

/**
 * Self-service account registration, mirroring the qs-status Angular
 * CreateAccountComponent.
 *
 * Backed by three endpoints on the remote care-api, all unauthenticated:
 *   GET  /v1/zones                        - timezone list
 *   GET  /v1/group-user/is-available      - username uniqueness (throttled)
 *   POST /v1/user                         - create the master account
 *
 * Uses plain fetch rather than EmfitAPI on purpose: none of these calls take a
 * token, and EmfitAPI's 401 handler clears storage and hard-redirects to '/',
 * which must never happen on a public signup page.
 */

export type TimeFormat = 'H24' | 'H12';
export type DateFormat = 'MM-DD' | 'DD-MM';

export interface CreateAccountData {
  agreement: boolean;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  locale: string;
  timezone_id: number;
  time_format: TimeFormat;
  date_format: DateFormat;
}

export type CreateAccountResult =
  | { ok: true }
  | {
      ok: false;
      error: string;
      /**
       * Set when the failure was traced to the username already existing, so
       * the caller can send the user back to the account step rather than
       * leaving them stuck on an unexplained error.
       */
      usernameTaken?: boolean;
    };

/** Europe/London — the default the Angular reference uses. */
export const DEFAULT_TIMEZONE_ID = 162;

/**
 * Pull a readable message out of a Nest error body:
 * { statusCode, message: string | string[], error }.
 */
function messageFromErrorBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const message = (body as { message?: unknown }).message;
  if (Array.isArray(message)) {
    const first = message.find((entry) => typeof entry === 'string');
    return (first as string) ?? null;
  }
  if (typeof message === 'string' && message.trim()) return message.trim();
  return null;
}

class RegistrationStoreClass {
  zones: Timezone[] = [];
  zonesLoading = false;
  submitting = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async fetchZones(): Promise<void> {
    if (this.zones.length > 0 || this.zonesLoading) return;
    this.zonesLoading = true;

    try {
      const response = await fetch(`${STATUS_API}/v1/zones`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to load timezones');
      const zones = (await response.json()) as Timezone[];
      runInAction(() => {
        this.zones = Array.isArray(zones) ? zones : [];
        this.zonesLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.zonesLoading = false;
      });
      console.error('Failed to fetch timezones:', error);
    }
  }

  /**
   * Best-effort match of the browser's timezone against the backend list,
   * falling back to Europe/London as the reference does.
   */
  get defaultTimezoneId(): number {
    try {
      const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const match = this.zones.find((zone) => zone.zone_name === browserZone);
      if (match) return match.zone_id;
    } catch {
      // Intl unavailable - fall through to the default
    }
    return DEFAULT_TIMEZONE_ID;
  }

  zoneName(zoneId: number): string | undefined {
    return this.zones.find((zone) => zone.zone_id === zoneId)?.zone_name;
  }

  async createAccount(data: CreateAccountData): Promise<CreateAccountResult> {
    // timezone_id must resolve to a real locale_zones row: the backend
    // dereferences it unguarded and 500s on an unknown id.
    const timezone = this.zoneName(data.timezone_id);
    if (!timezone) {
      return { ok: false, error: t('Please select a valid timezone.') };
    }

    runInAction(() => {
      this.submitting = true;
      this.error = null;
    });

    try {
      const response = await fetch(`${STATUS_API}/v1/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ ...data, timezone }),
      });

      if (!response.ok) {
        let message: string | null = null;
        try {
          message = messageFromErrorBody(await response.json());
        } catch {
          // Non-JSON error body
        }

        // app_users.username carries a unique index, but createAccountUser
        // does not catch the violation - it surfaces as a bare 500 with no
        // message. The availability probe also falls open on a 429 and can
        // race, so re-check before blaming the network.
        if (!message && (response.status === 409 || response.status >= 500)) {
          forgetLoginAvailability(data.username);
          const availability = await checkLoginAvailable(data.username);
          if (!availability.indeterminate && !availability.available) {
            const taken = t(
              'The username is already taken. Please choose another one.'
            );
            runInAction(() => {
              this.error = taken;
              this.submitting = false;
            });
            return { ok: false, error: taken, usernameTaken: true };
          }
        }

        const error =
          message || t('Could not create the account. Please try again.');
        runInAction(() => {
          this.error = error;
          this.submitting = false;
        });
        return { ok: false, error };
      }

      runInAction(() => {
        this.submitting = false;
      });
      return { ok: true };
    } catch (error) {
      // Surface a translated generic rather than a raw fetch error such as
      // "Failed to fetch", which is untranslated and means nothing to a user.
      console.error('Account creation request failed:', error);
      const message = t('Could not reach the server. Please try again.');
      runInAction(() => {
        this.error = message;
        this.submitting = false;
      });
      return { ok: false, error: message };
    }
  }

  reset(): void {
    runInAction(() => {
      this.error = null;
      this.submitting = false;
    });
  }
}

export const RegistrationStore = new RegistrationStoreClass();
